// @ts-nocheck
// mockStream 은 historical legacy mock 데이터를 들고 있다 (구 contract 의 키들).
// 외부 (SSE 이벤트) 로 emit 할 때만 새 contract 로 변환 (`migrateTaskEditorStateFromLegacy`).
// 내부는 type-check 무시 — dev mock 한정.
import type {
  ActionRequest,
  Decision,
  ResumeRequest,
  SSEEvent,
  StreamRequest,
} from "./types";
import { migrateTaskEditorStateFromLegacy } from "../utils/taskEditorUtils";

interface MockSession {
  currentTask: TaskEditorState | null;
  pendingAction?: ActionRequest;
}

const sessions = new Map<string, MockSession>();

function getSession(threadId: string): MockSession {
  let s = sessions.get(threadId);
  if (!s) {
    s = { currentTask: null };
    sessions.set(threadId, s);
  }
  return s;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

const rid = () => Math.random().toString(36).slice(2, 10);

function ev<E extends SSEEvent["event"]>(
  event: E,
  data: Extract<SSEEvent, { event: E }>["data"]
): SSEEvent {
  // task_patch / final 의 task 필드는 legacy mock 데이터이므로 새 contract 로 변환해서 emit.
  if (event === "task_patch") {
    const d = data as { thread_id: string; task: unknown };
    return {
      event: "task_patch",
      data: {
        thread_id: d.thread_id,
        task: migrateTaskEditorStateFromLegacy(d.task),
      },
    } as SSEEvent;
  }
  if (event === "final") {
    const d = data as {
      thread_id: string;
      assistant_message: string;
      task: unknown;
      task_changed: boolean;
    };
    return {
      event: "final",
      data: {
        thread_id: d.thread_id,
        assistant_message: d.assistant_message,
        task: d.task ? migrateTaskEditorStateFromLegacy(d.task) : null,
        task_changed: d.task_changed,
      },
    } as SSEEvent;
  }
  return { event, data } as SSEEvent;
}

function chunkText(text: string): string[] {
  const chunks: string[] = [];
  let buf = "";
  for (const ch of text) {
    buf += ch;
    if (buf.length >= 2 + Math.floor(Math.random() * 3)) {
      chunks.push(buf);
      buf = "";
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}

// =========================
// Mock guide previews
// =========================

const MOCK_GUIDES = {
  components: `# TaskEditor 컴포넌트 카탈로그

## 블록 (block.type)
| type        | 역할            | content                                | lines        |
|-------------|-----------------|----------------------------------------|--------------|
| Header1     | 섹션 제목       | 한 줄 제목 텍스트                      | 없음         |
| Instruction | 본문 단위 묶음  | null (라인이 본문)                     | 1개 이상 셀  |
| LastCard    | 활동지 종료     | "common:lastCard1\\ncommon:lastCard2"   | 없음         |

마지막 블록은 반드시 LastCard, id="task-editor-last-card" 고정.

## 셀 (line.type) — 설명 파트 / 액션 파트
한 Instruction 블록은 설명 파트 (Typography) 를 최소 1개 가져야 함.

| type             | 파트              | 용도                              | 주요 필드 |
|------------------|-------------------|-----------------------------------|-----------|
| Typography       | 설명 (필수 텍스트)| 텍스트 출력                       | content, typographyStyle |
| Image            | 설명              | 이미지                            | content (URL) |
| AutomaticThought | 설명 (불러오기)   | 자동적 사고 mock                  | (도메인 전용) |
| Textarea         | 액션 (인풋)       | 내담자 자유 입력                  | content (snake_case binding), textareaPlaceholder |
| Preset           | 액션 (인풋)       | 코치 전용 미리채움                | content (snake_case binding) |
| ButtonGroup      | 액션 (선택·분기) | 분기형 버튼                       | options[] showFromId/hideFromId |
| ButtonSelection  | 액션 (선택)       | 단순 선택 (분기 없음)             | options[], allowMultipleSelection |
| Quiz             | 액션 (선택)       | 정답이 있는 선택                  | options[] 중 최소 1개 isCorrect:true |

## Typography 스타일
- Body: 일반 본문/안내 (기본)
- Label: 입력 필드 바로 위 질문/라벨
- Alert: 주의·중요 안내
- Dimmed: 부가 설명·힌트

## ButtonGroup 옵션 필드
text / showFromId (옵션 누르면 해당 블록부터 노출) / showToId / hideFromId / restartFromId / isCorrect (Quiz 전용)
— showFromId/hideFromId 등이 가리키는 블록 ID 는 실재해야 함. 매칭 실패 시 검수 단계 거부.

## Quiz 규칙
- options[] 에 최소 1개 isCorrect:true
- 정답 해설은 Quiz 바로 다음 블록의 Typography(Body) 로 제공`,

  structure: `# 활동지 구조 원칙

## 최상위 필드 (TaskEditorState)
- taskId: 영문 kebab-case 또는 숫자 식별자 (예: "day1-start-of-change")
- title: 한국어 짧은 제목
- setNumber: 정수 또는 null
- isKeyActivity: bool 또는 null
- blocks: 비어 있으면 안 됨

## ID 명명 규칙
- block.id: "block-<설명>" 또는 "task-editor-block-<slug>"
- line.id: "line-<설명>" 또는 "task-editor-line-<slug>"
- Textarea/Preset content (데이터 바인딩): 영문 snake_case (예: situation_input)
- LastCard.id: 고정 "task-editor-last-card"
- 한 활동지 안 모든 block.id / line.id 고유
- 기존 활동지 편집 시 기존 id 최대한 보존

## 블록 위계 (설계도)
Instruction 블록
├── 설명 파트 (REQUIRED): Typography 최소 1개 + 옵셔널 Image / AutomaticThought
└── 유저 액션 파트 (OPTIONAL): Textarea, Preset, ButtonGroup, ButtonSelection, Quiz

설명 파트 없이 액션 파트만 있으면 검증 실패 (POST /task/drafts 422).
일반적으로 설명 라인 먼저 → 액션 라인 뒤.

## 블록 구성 패턴
1. Header1 로 섹션 시작
2. 본문은 Instruction 안 Typography(Body)
3. 입력 필드 앞: Typography(Label) 로 질문
4. 주의·강조: Typography(Alert)
5. Quiz 다음에는 정답 해설 블록
6. 마지막은 LastCard

## ButtonGroup 분기 규칙
- showFromId/hideFromId/showToId/restartFromId 는 실재 블록 id
- 분기 대상 블록은 ButtonGroup 바로 뒤
- 모든 옵션 경로가 결국 LastCard 로 수렴

## 편집 모드
- taskId, 블록·라인 구조, ID, snake_case binding 최대한 보존
- 사용자가 변경 요구 안 한 부분은 건드리지 않기`,

  styles: `# Typography 스타일

- Body: 일반 본문/안내 (기본)
- Label: 입력 필드 바로 위 질문/라벨
- Alert: 주의·중요 안내
- Dimmed: 부가 설명·힌트

Quiz/ButtonSelection/Textarea 등 액션 라인 직전에는 Label 권장.`,
};

// =========================
// Mutation helpers
// =========================

function ensureLastCardLast(blocks: TaskEditorBlock[]): TaskEditorBlock[] {
  const lastCard = blocks.find((b) => b.type === "LastCard");
  const others = blocks.filter((b) => b.type !== "LastCard");
  return [
    ...others,
    lastCard ?? {
      id: "task-editor-last-card",
      type: "LastCard",
      content: "common:lastCard1\ncommon:lastCard2",
    },
  ];
}

function insertBeforeLastCard(
  task: TaskEditorState,
  newBlock: TaskEditorBlock
): TaskEditorState {
  const lastCardIdx = task.blocks.findIndex((b) => b.type === "LastCard");
  const insertIdx = lastCardIdx >= 0 ? lastCardIdx : task.blocks.length;
  return {
    ...task,
    blocks: [
      ...task.blocks.slice(0, insertIdx),
      newBlock,
      ...task.blocks.slice(insertIdx),
    ],
  };
}

function findLastInstructionBlock(task: TaskEditorState): {
  block: TaskEditorBlock;
  index: number;
} | null {
  for (let i = task.blocks.length - 1; i >= 0; i--) {
    if (task.blocks[i].type === "Instruction") {
      return { block: task.blocks[i], index: i };
    }
  }
  return null;
}

function withAddedLine(
  task: TaskEditorState,
  newComponent: TaskEditorComponent,
  fallbackBlock?: TaskEditorBlock
): TaskEditorState {
  const found = findLastInstructionBlock(task);
  if (found) {
    const updatedBlock: TaskEditorBlock = {
      ...found.block,
      lines: [...(found.block.lines ?? []), newComponent],
    };
    return {
      ...task,
      blocks: task.blocks.map((b, i) => (i === found.index ? updatedBlock : b)),
    };
  }
  if (fallbackBlock) {
    return insertBeforeLastCard(task, fallbackBlock);
  }
  return task;
}

function buildSeedTask(): TaskEditorState {
  return {
    taskId: "mock-task-" + rid(),
    title: "(mock) 새 활동지",
    setNumber: 1,
    isKeyActivity: false,
    blocks: [
      {
        id: "block-intro-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "여기 본문이 들어갑니다.",
            typographyStyle: "Body",
          },
        ],
      },
      {
        id: "task-editor-last-card",
        type: "LastCard",
        content: "common:lastCard1\ncommon:lastCard2",
      },
    ],
  };
}

function applyMockEdit(
  task: TaskEditorState | null,
  action: { name: string; args: Record<string, unknown> }
): TaskEditorState | null {
  if (action.name === "write_file") {
    const content = action.args.content;
    if (typeof content === "string") {
      try {
        return JSON.parse(content) as TaskEditorState;
      } catch {
        return task;
      }
    }
    return task;
  }
  if (action.name === "edit_file" && task) {
    const oldStr = String(action.args.old_string ?? "");
    const newStr = String(action.args.new_string ?? "");
    const json = JSON.stringify(task);
    const replaced = json.split(oldStr).join(newStr);
    try {
      return JSON.parse(replaced) as TaskEditorState;
    } catch {
      return task;
    }
  }
  return task;
}

// =========================
// Scenario routing
// =========================

type Scenario =
  | "rawToBeginning"
  | "rawToDistancing"
  | "rawToExperienceSeparation"
  | "branchingFlow"
  | "validateTask"
  | "addQuiz"
  | "removeLastBlock"
  | "generic";

function pickScenario(message: string): Scenario {
  const m = message.toLowerCase();

  // 분기 / branching
  if (/(분기|buttongroup .?(추가|넣)|branching|갈래)/.test(m)) {
    return "branchingFlow";
  }
  // 검증 / lint
  if (/(검증|validate|lint|규칙.?확인|체크.?해)/.test(m)) {
    return "validateTask";
  }
  // 긴 글 (200자 이상) 또는 명시적 키워드 → raw 변환
  if (message.length >= 200 || /활동지로 만들|구조화|블록으로 나눠/.test(m)) {
    // 경험 분리 (실수 예시 / 경험 꾸러미)
    if (
      /경험 분리|경험 ?꾸러미|악순환|실전 연습|경험분리|복잡한 경험/.test(
        message
      )
    ) {
      return "rawToExperienceSeparation";
    }
    // 거리두기 (선글라스 / 신성한 멈춤 / RAIN)
    if (
      /거리두기|마음의 선글라스|신성한 멈춤|마음의 rain|건강한 마음에 대한 오해|새로운 관계/i.test(
        message
      )
    ) {
      return "rawToDistancing";
    }
    // 변화의 시작 (Day 1)
    if (
      /변화의 시작|위대한 선택|마법의 알약|오래된 펌프|100일|마음속 타인/.test(
        message
      )
    ) {
      return "rawToBeginning";
    }
    // fallback
    return "rawToBeginning";
  }
  if (/(quiz|퀴즈)/.test(m)) return "addQuiz";
  if (/(삭제|remove|delete|지우|제거)/.test(m)) return "removeLastBlock";
  return "generic";
}

// =========================
// Stream entry
// =========================

export async function* mockStreamTaskAuthoring(
  req: StreamRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const tid = req.thread_id;
  const session = getSession(tid);

  // 매 turn 마다 최신 current_task 로 동기화 (사용자 편집 반영)
  if (req.current_task !== undefined) {
    session.currentTask = req.current_task;
  }

  const scenario = pickScenario(req.message);

  yield ev("session", { thread_id: tid });
  await sleep(180, signal);

  switch (scenario) {
    case "rawToBeginning":
      yield* rawToBeginningScenario(tid, req.message, signal);
      return;
    case "rawToDistancing":
      yield* rawToDistancingScenario(tid, req.message, signal);
      return;
    case "rawToExperienceSeparation":
      yield* rawToExperienceSeparationScenario(tid, req.message, signal);
      return;
    case "branchingFlow":
      yield* branchingFlowScenario(tid, signal);
      return;
    case "validateTask":
      yield* validateTaskScenario(tid, signal);
      return;
    case "addQuiz":
      yield* addQuizScenario(tid, signal);
      return;
    case "removeLastBlock":
      yield* removeLastBlockScenario(tid, signal);
      return;
    default:
      yield* genericScenario(tid, signal);
      return;
  }
}

// =========================
// Common helpers
// =========================

function ensureCurrentTask(session: MockSession): TaskEditorState {
  if (!session.currentTask) {
    session.currentTask = buildSeedTask();
  }
  return session.currentTask;
}

async function* streamReply(
  reply: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  for (const c of chunkText(reply)) {
    if (signal?.aborted) return;
    yield ev("message_chunk", { delta: c });
    await sleep(35, signal);
  }
}

async function* toolReadFile(
  filePath: string,
  preview: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  yield ev("tool_call", { name: "read_file", args: { file_path: filePath } });
  await sleep(380 + Math.random() * 250, signal);
  yield ev("tool_result", { name: "read_file", preview });
}

function debugEvent(tid: string, t0: number): SSEEvent {
  return ev("debug", {
    thread_id: tid,
    inference_id: "mock-inf-" + rid(),
    trace_id: "mock-tr-" + rid(),
    execution_time_ms: Date.now() - t0,
  });
}

function pendingApprovalEvent(
  tid: string,
  action: ActionRequest,
  allowed: ("approve" | "edit" | "reject")[] = ["approve", "edit", "reject"]
): SSEEvent {
  return ev("pending_approval", {
    thread_id: tid,
    interrupt_id: "mock-int-" + rid(),
    action_requests: [action],
    review_configs: [{ action_name: action.name, allowed_decisions: allowed }],
  });
}

function writeFileAction(
  task: TaskEditorState,
  description: string
): ActionRequest {
  return {
    name: "write_file",
    args: {
      file_path: "/task.json",
      content: JSON.stringify(task, null, 2),
    },
    description,
  };
}

// =========================
// Scenario: Add Quiz line to last Instruction block
// =========================

async function* addQuizScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile("/guides/components.md", MOCK_GUIDES.components, signal);

  yield* streamReply(
    "마지막 Instruction 블록에 Quiz 컴포넌트를 추가할게요. " +
      "옵션 3개 중 1개를 정답으로 표시해두었습니다.",
    signal
  );

  const base = ensureCurrentTask(session);
  const quizSeq =
    base.blocks.reduce(
      (acc, b) => acc + (b.lines?.filter((l) => l.type === "Quiz").length ?? 0),
      0
    ) + 1;

  const newComponent: TaskEditorComponent = {
    id: "line-" + rid(),
    type: "Quiz",
    content: `quiz_${quizSeq}`,
    options: [
      { text: "오답 1", isCorrect: false },
      { text: "정답", isCorrect: true },
      { text: "오답 2", isCorrect: false },
    ],
    incorrectMessage: "다시 한 번 생각해보세요",
  };

  let nextTask: TaskEditorState;
  const found = findLastInstructionBlock(base);
  if (found) {
    nextTask = withAddedLine(base, newComponent);
  } else {
    nextTask = insertBeforeLastCard(base, {
      id: "block-instruction-" + rid(),
      type: "Instruction",
      content: "",
      lines: [newComponent],
    });
  }
  session.currentTask = nextTask;

  const action = writeFileAction(
    nextTask,
    `Quiz 컴포넌트 추가 (content="quiz_${quizSeq}", 옵션 3개, 정답 index 1)`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Remove last block (before LastCard)
// =========================

async function* removeLastBlockScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile("/task.json", "(현재 task 구조 확인)", signal);

  const base = ensureCurrentTask(session);
  const lastCardIdx = base.blocks.findIndex((b) => b.type === "LastCard");
  const removableIdx = lastCardIdx > 0 ? lastCardIdx - 1 : -1;

  if (removableIdx < 0) {
    yield* streamReply(
      "삭제할 수 있는 블록이 없어요. LastCard 만 남아 있네요.",
      signal
    );
    yield ev("final", {
      thread_id: tid,
      assistant_message: "삭제할 블록이 없습니다.",
      task: base,
      task_changed: false,
    });
    yield debugEvent(tid, t0);
    return;
  }

  const target = base.blocks[removableIdx];
  const blockLabel =
    target.type === "Header1"
      ? `Header1 ("${target.content.slice(0, 20)}")`
      : `Instruction (line ${target.lines?.length ?? 0}개)`;

  yield* streamReply(
    `LastCard 직전 ${blockLabel} 을 삭제할게요. id="${target.id}".`,
    signal
  );

  const nextTask: TaskEditorState = {
    ...base,
    blocks: [
      ...base.blocks.slice(0, removableIdx),
      ...base.blocks.slice(removableIdx + 1),
    ],
  };
  session.currentTask = nextTask;

  const action = writeFileAction(nextTask, `${blockLabel} 블록 1개 삭제`);
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// =========================
// Scenario: Raw text → "거리두기란 무엇인가" 활동지
// =========================

function buildDistancingTask(): TaskEditorState {
  return {
    taskId: "mock-distancing-" + rid().slice(0, 4),
    title: "거리두기란 무엇인가",
    setNumber: 2,
    isKeyActivity: true,
    blocks: [
      {
        id: "block-title-" + rid(),
        type: "Header1",
        content: "거리두기란 무엇인가",
      },
      // 마음의 선글라스
      {
        id: "block-h-sunglasses-" + rid(),
        type: "Header1",
        content: "마음의 선글라스",
      },
      {
        id: "block-sunglasses-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "마음이 건강하지 않을 때는 세상을 마치 새까만 선글라스를 끼고 보는 것과 같습니다. 아무리 기분 좋은 일이 생겨도 결국 불안이나 걱정, 깊은 무력감에 빠지고 맙니다. 뇌가 모든 상황을 부정적으로 해석하도록 습관이 들어버렸기 때문입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이런 상태에서는 머릿속에 떠오르는 부정적인 생각들이 모두 변함없는 진실처럼 느껴집니다. 그 생각들을 곧 '나 자신'이라고 굳게 믿으며 스스로를 옭아매고 자책하게 되죠. 혹시 지금 이렇게 어둡고 긴 터널을 홀로 걷고 계시나요?",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "그렇다면 정말 잘 찾아오셨습니다. 이제부터 그 까만 마음의 선글라스를 벗고 본래의 삶을 되찾는 연습을 시작해보려 합니다. 나를 괴롭히던 그 생각들이 사실 '나' 자신이 아니었다는 것을 자연스럽게 깨닫게 될 것입니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 신성한 멈춤
      {
        id: "block-h-pause-" + rid(),
        type: "Header1",
        content: "신성한 멈춤",
      },
      {
        id: "block-pause-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "생각은 순식간에 지나갑니다. 그런 생각을 미처 알아차리기도 전에 우린 이미 그에 반응해서 감정과 감각을 느끼며 어떤 행동을 마친 상태이지요. 이렇게 무의식적으로 반응하는 악순환을 끊어내려면 가장 먼저 '잠시 멈추는 연습'이 필요합니다. 이를 일상 속의 '신성한 멈춤'이라고 합니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "신성한 멈춤을 갖게 되면, 생각과 자동 반응 사이의 연결고리가 끊어집니다. 비로소 내 진짜 감정이 두려움인지 슬픔인지 한 걸음 물러서서 관찰할 수 있는 틈이 생기는 것이죠. 다툼이나 갈등이 일어나는 순간에도, 이 멈춤은 현명한 선택을 내릴 수 있는 마음의 여유를 만들어줍니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이러한 멈춤은 마음이 비교적 평온할 때 일상 속에서 미리 연습해 두는 것이 좋습니다. 평소에 충분히 훈련해 두면, 거대한 스트레스가 닥쳐와도 흔들림 없이 차분하게 대처할 수 있습니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // Quiz: pause_quiz (정답 D)
      {
        id: "block-quiz-pause-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "무의식적인 반응을 끊어내기 위해 가장 먼저 해야 할 일은 무엇일까요?",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Quiz",
            content: "pause_quiz",
            options: [
              { text: "A. 내 잘못이 무엇인지 자책한다.", isCorrect: false },
              { text: "B. 반박할 말을 머릿속으로 계산한다.", isCorrect: false },
              { text: "C. 긍정적인 상황이라고 세뇌한다.", isCorrect: false },
              {
                text: "D. 마음속에서 일어나는 일들을 관찰한다.",
                isCorrect: true,
              },
            ],
            incorrectMessage: "활동지를 천천히 살펴본 뒤 다시 답해보세요.",
          },
        ],
      },
      {
        id: "block-answer-pause-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "정답은 D입니다. '신성한 멈춤'을 통해 습관적으로 반응하는 연결고리를 끊어낼 수 있습니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 마음의 RAIN
      {
        id: "block-h-rain-" + rid(),
        type: "Header1",
        content: "마음의 RAIN",
      },
      {
        id: "block-rain-intro-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "그렇다면 이 '신성한 멈춤'은 어떻게 실천할 수 있을까요? 신성한 멈춤은 RAIN이라는 방법을 통해 실천할 수 있습니다. RAIN의 과정은 다음과 같습니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // RAIN 4 stages
      {
        id: "block-rain-stages-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "인지하기 (R, Recognition)",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "내 마음에서 무슨 일이 일어나는지 알아차리기",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "수용하기 (A, Acceptance)",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "마음속에 떠오른 감정이나 생각을 거부하지 않고 온전히 받아들이기",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "살펴보기 (I, Investigation)",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "그 마음의 움직임을 어린아이와 같은 호기심으로 가만히 살펴보기",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "탈동일시 (N, Non-identification)",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "그 생각들을 나 자신과 동일시하지 않기",
            typographyStyle: "Body",
          },
        ],
      },
      {
        id: "block-rain-wrap-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이 네 가지 단계를 거치다 보면, 거센 생각의 소용돌이 속에서도 내가 진정으로 원하는 삶의 방향을 선택할 힘을 얻게 됩니다. 마음에 비바람이 몰아칠 때마다 이 'RAIN'을 떠올려 보세요. 해묵은 마음의 습관과 문제 행동에서 벗어나 자유로워지는 열쇠가 되어줄 것입니다. 앞으로 이 네 가지 과정에 따라 거리두기 기술을 차근차근 연습해 보겠습니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 건강한 마음에 대한 오해
      {
        id: "block-h-misunderstand-" + rid(),
        type: "Header1",
        content: "건강한 마음에 대한 오해",
      },
      {
        id: "block-misunderstand-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "흔히 마음이 건강해진다는 것을 '늘 긍정적인 생각만 가득한 상태'로 오해하곤 합니다. 하지만 억지로 긍정적인 생각만 하려 애쓰는 것은 그리 좋은 방법이 아닙니다. 일시적인 눈속임에 불과할 뿐만 아니라, 오히려 문제를 악화시킬 수 있기 때문입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "가끔 \"아무 생각도 하지 마. 그럼 편해져\"라고 말하는 사람들도 있습니다. 하지만 생각을 완전히 없애는 것은 애초에 불가능합니다. 사람의 뇌는 무언가를 '지우는' 방식으로는 작동하지 않으니까요. 따라서 이 역시 효과적인 해결책이 될 수 없습니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이 부분은 나중에 더 자세히 다루겠습니다. 지금은 앞으로 연습할 내용이 '무조건 긍정적으로 생각하기'나 '아무 생각도 하지 않기'가 아니라는 점만 기억해 두시면 충분합니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 새로운 관계
      {
        id: "block-h-newrel-" + rid(),
        type: "Header1",
        content: "새로운 관계",
      },
      {
        id: "block-newrel-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "우리를 견딜 수 없이 힘들게 만드는 진짜 원인은 슬픔이나 불안 같은 '감정' 그 자체가 아닙니다. 그 감정을 다루고 관계 맺는 '방식'에 있습니다. 부정적인 감정이나 생각을 억누르고 없애려고만 하면, 그것들은 더 강하게 마음을 옭아맵니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이제부터는 억지로 생각을 지우려 애쓰는 대신, 마음속에 떠오르는 생각들과 '건강하게 관계 맺는 연습'을 해나갈 것입니다. 생각과 적당한 거리를 두고 유연하게 대처할 수 있게 된다면, 앞으로 어떤 시련이 닥쳐와도 내 삶을 지혜롭게 이끌어갈 수 있을 것입니다.",
            typographyStyle: "Body",
          },
        ],
      },
      {
        id: "block-quiz-newrel-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "오늘부터 새롭게 연습해 나갈 내용은 무엇일까요?",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Quiz",
            content: "newrel_quiz",
            options: [
              {
                text: "A. 매일 스스로에게 긍정적인 말 해주기",
                isCorrect: false,
              },
              { text: "B. 아무 생각 없이 머릿속을 비워내기", isCorrect: false },
              {
                text: "C. 마음속 생각 및 감정들과 건강한 관계 맺기",
                isCorrect: true,
              },
            ],
            incorrectMessage: "활동지를 천천히 살펴본 뒤 다시 답해보세요.",
          },
        ],
      },
      {
        id: "block-answer-newrel-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "정답은 C입니다. 억지로 긍정적인 생각을 하거나 생각을 지우는 것은 불가능하며 효과적이지도 않습니다. 유일한 해결책은 내 생각과 감정을 대하는 관점을 바꾸는 것입니다.",
            typographyStyle: "Body",
          },
        ],
      },
      {
        id: "task-editor-last-card",
        type: "LastCard",
        content: "common:lastCard1\ncommon:lastCard2",
      },
    ],
  };
}

async function* rawToDistancingScenario(
  tid: string,
  rawText: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield ev("tool_call", { name: "ls", args: { path: "/guides" } });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "ls",
    preview:
      "00-overview.md\n01-components.md\n02-structure.md\n03-tone.md\n04-styles.md\nexamples/\n  day1-start-of-change.json\n  day2-distancing.json\n  day3-experience-separation.json",
  });

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/examples/day2-distancing.json",
    '{\n  taskId: "day2-distancing",\n  title: "거리두기란 무엇인가",\n  blocks: [Header1 + 섹션 5개 (마음의 선글라스 / 신성한 멈춤 / 마음의 RAIN / 건강한 마음에 대한 오해 / 새로운 관계), Quiz × 2, 해설 × 2, LastCard]\n}',
    signal
  );

  const charCount = rawText.length;

  yield* streamReply(
    `${charCount}자, "거리두기란 무엇인가" 활동지로 분석됩니다.\n\n` +
      `소스 마크업 매핑:\n` +
      `· "소제목" → 섹션 Header1\n` +
      `· "기본" → Typography(Body)\n` +
      `· "버튼" + 선택지 + "활동지를 천천히 살펴본 뒤..." → Quiz (incorrectMessage 포함)\n` +
      `· RAIN 4단계 (R/A/I/N) 정의 블록 → Label + Body 페어 × 4 를 하나의 Instruction 안에 배치\n\n` +
      `구조 설계:\n` +
      `• Header1 × 6 — 활동지 제목 + 5섹션\n` +
      `• Instruction × 11 — 본문 + RAIN 정의 + Quiz × 2 + 해설 × 2 + 마무리 1\n` +
      `• Quiz × 2 — pause_quiz (정답 D, 4 options), newrel_quiz (정답 C, 3 options). 모두 incorrectMessage 동일.\n` +
      `• LastCard\n\n` +
      `RAIN 정의는 Label-Body 페어로 4번 반복해 시각적 위계를 확보했습니다.`,
    signal
  );

  const draftTask = buildDistancingTask();
  session.currentTask = draftTask;

  const action = writeFileAction(
    draftTask,
    `"거리두기란 무엇인가" 활동지 — 17개 블록. ` +
      `Header1 × 6, Instruction × 10, LastCard. ` +
      `Quiz × 2 (pause_quiz / newrel_quiz). RAIN 4 stages 위계.`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Raw text → "기술 1: 경험 분리" 활동지
// =========================

function buildExperienceSeparationTask(): TaskEditorState {
  return {
    taskId: "mock-experience-separation-" + rid().slice(0, 4),
    title: "기술 1: 경험 분리",
    setNumber: 3,
    isKeyActivity: true,
    blocks: [
      {
        id: "block-title-" + rid(),
        type: "Header1",
        content: "기술 1: 경험 분리",
      },
      // 첫 번째 훈련법
      {
        id: "block-h-first-" + rid(),
        type: "Header1",
        content: "첫 번째 훈련법",
      },
      {
        id: "block-first-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "지난 시간에 마음을 인지하는 두 가지 훈련 방법이 있다고 배웠습니다. 기록을 통해 경험을 분리하는 연습과, 명상으로 주의력을 통제하는 훈련이었죠. 오늘은 그중 첫 번째 연습을 본격적으로 시작해 보겠습니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이번 시간에 가장 먼저 배울 핵심 개념은 심리학에서 매우 중요하게 다루는 '경험 분리'입니다. 마음의 작용을 이해하기 위한 아주 기초적인 작업입니다. 스포츠나 악기를 처음 배울 때 기본기가 중요한 것처럼, 이 기본기만 확실히 익혀두어도 마음과 거리 두는 능력이 몰라보게 탄탄해질 것입니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 복잡한 경험 꾸러미
      {
        id: "block-h-bundle-" + rid(),
        type: "Header1",
        content: "복잡한 경험 꾸러미",
      },
      {
        id: "block-bundle-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "마음속에 떠오르는 부정적인 생각과 거리를 둔 채 가만히 바라보기란 말처럼 쉽지 않습니다. 현실에서 마주하는 내면의 경험이 단순히 '생각'이라는 단일한 덩어리로만 이루어져 있지 않기 때문입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "사실 마음속에서는 생각과 감정, 신체 반응, 그리고 행동과 충동이 복잡하게 뒤섞여 하나의 거대한 '꾸러미'처럼 한꺼번에 쏟아집니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Image",
            content:
              "https://placehold.co/600x300?text=상황+%E2%86%92+경험+꾸러미",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              '예를 들어 직장에서 무언가 실수를 한 상황을 떠올려 볼까요? "이번 프로젝트는 나 때문에 다 망했어"라는 생각과 동시에 죄책감이나 불안 같은 강렬한 감정이 파도처럼 밀려옵니다. 가슴이 답답해지거나 머리가 아픈 증상이 나타나기도 하고, 당장이라도 다 포기하고 도망치고 싶다는 충동까지 일어납니다. 이 모든 것이 하나의 꾸러미로 묶여서 다가오는 것이죠.',
            typographyStyle: "Body",
          },
        ],
      },
      // Quiz: bundle_quiz (정답 D)
      {
        id: "block-quiz-bundle-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "다음 중 내면에서 뭉쳐지는 '경험 꾸러미'의 구성 요소가 아닌 것은 무엇일까요?",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Quiz",
            content: "bundle_quiz",
            options: [
              {
                text: "A. 머릿속을 스쳐가는 부정적인 '생각'",
                isCorrect: false,
              },
              { text: "B. 불안하고 초조한 '감정'", isCorrect: false },
              { text: "C. 도망치고 싶어지는 '충동'", isCorrect: false },
              { text: "D. 실수를 저지른 객관적인 '상황'", isCorrect: true },
            ],
            incorrectMessage: "활동지를 천천히 살펴본 뒤 다시 답해보세요.",
          },
        ],
      },
      {
        id: "block-answer-bundle-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "정답은 D입니다. 실수를 저지른 '상황'은 내면의 반응을 촉발하는 외부 사건일 뿐, 경험 꾸러미에 속하지 않습니다. 경험 꾸러미는 특정 상황을 마주했을 때 마음속에서 덩어리져 일어나는 생각, 감정/신체 반응, 행동/충동으로 구성됩니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 끝없는 악순환
      {
        id: "block-h-loop-" + rid(),
        type: "Header1",
        content: "끝없는 악순환",
      },
      {
        id: "block-loop-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "경험 꾸러미 속의 생각, 감정과 신체 반응, 행동과 충동은 멈춰 있지 않고 서로 끊임없이 영향을 주고받습니다. 부정적인 생각이 불안을 증폭시키고, 그 불안은 호흡을 가쁘게 만들어 더 극단적인 생각을 불러오기도 합니다. 마치 엉켜버린 털실 타래처럼 단단하게 뭉쳐서 끝없는 악순환을 만들어냅니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이렇게 얽힌 꾸러미를 그대로 둔 채 생각과 거리를 두기란 거의 불가능합니다. 마음속에 정확히 어떤 생각이 스쳐 지나갔는지 파악하기 어려울 뿐만 아니라, 막연한 감정의 크기가 너무 압도적으로 느껴지기 때문입니다. 따라서 이 거대한 덩어리를 각각의 개별 요소로 가닥가닥 쪼개어 바라보는 연습이 필요합니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "위의 직장에서 무언가 실수를 한 상황 예시에서 '경험 꾸러미'를 분리한 모습은 다음과 같습니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 4 분리 요소 (Label + Body × 4)
      {
        id: "block-decompose-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "생각",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "이번 프로젝트는 나 때문에 다 망했어",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "감정",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "죄책감, 불안",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "신체 반응",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "가슴이 답답함, 머리가 아픔",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "행동/충동",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content: "포기하고 도망치고 싶었음",
            typographyStyle: "Body",
          },
        ],
      },
      // 실전 연습
      {
        id: "block-h-practice-" + rid(),
        type: "Header1",
        content: "실전 연습",
      },
      {
        id: "block-practice-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이처럼 얽혀 있는 마음의 덩어리를 개별 요소로 해체하는 과정을 '경험 분리'라고 부릅니다. 방법은 간단하지만 효과는 매우 강력합니다. 압도적인 감정에 휩쓸리지 않고 마음을 객관적으로 바라볼 수 있게 돕기 때문입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "앞으로 벅차고 강렬한 감정을 마주하더라도, 일기장에 감정을 쏟아내듯 무작정 적어 내려가는 것은 피하는 게 좋습니다. 그 대신, 객관적인 '상황', 머릿속을 스쳐 간 '생각', 느껴진 '감정과 신체 반응', 그리고 '충동이나 실제 행동'을 명확히 나누어 기록해 보세요.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이렇게 항목을 구분해 적는 것만으로도 혼란스러웠던 경험 꾸러미의 힘이 약해지고 마음에 여유가 생기는 것을 느낄 수 있습니다. 자, 그럼 다음 활동에서 이 '경험 분리'를 구체적으로 어떻게 실천할 수 있는지 함께 알아보겠습니다.",
            typographyStyle: "Body",
          },
        ],
      },
      {
        id: "task-editor-last-card",
        type: "LastCard",
        content: "common:lastCard1\ncommon:lastCard2",
      },
    ],
  };
}

async function* rawToExperienceSeparationScenario(
  tid: string,
  rawText: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield ev("tool_call", { name: "ls", args: { path: "/guides" } });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "ls",
    preview:
      "00-overview.md\n01-components.md\n02-structure.md\n03-tone.md\n04-styles.md\nexamples/\n  day1-start-of-change.json\n  day2-distancing.json\n  day3-experience-separation.json",
  });

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/examples/day3-experience-separation.json",
    '{\n  taskId: "day3-experience-separation",\n  title: "기술 1: 경험 분리",\n  blocks: [Header1 + 4 섹션, Image (꾸러미 도식), Quiz × 1, 경험 분리 예시 (Label+Body × 4 페어), LastCard]\n}',
    signal
  );

  const charCount = rawText.length;

  yield* streamReply(
    `${charCount}자, "기술 1: 경험 분리" 활동지로 분석됩니다.\n\n` +
      `소스 마크업의 특이점:\n` +
      `· 🖼️ (상황 → 경험 꾸러미) 이미지 placeholder → Image 라인으로 매핑 (https://placehold.co URL)\n` +
      `· "생각/감정/신체 반응/행동·충동" 4-항목 분리 예시 → Label + Body 페어 × 4 를 하나의 Instruction 안에 배치 (위계 명확)\n` +
      `· "버튼" + 4 선택지 → Quiz (정답 D, 4 options, incorrectMessage 포함)\n\n` +
      `구조 설계:\n` +
      `• Header1 × 5 — 활동지 제목 + 4섹션 (첫 번째 훈련법 / 복잡한 경험 꾸러미 / 끝없는 악순환 / 실전 연습)\n` +
      `• Instruction × 8 — 본문 4개 + 이미지 포함 본문 + Quiz + 해설 + 4-항목 분리 예시\n` +
      `• Image × 1, Quiz × 1 (bundle_quiz, 정답 D)\n` +
      `• LastCard`,
    signal
  );

  const draftTask = buildExperienceSeparationTask();
  session.currentTask = draftTask;

  const action = writeFileAction(
    draftTask,
    `"기술 1: 경험 분리" 활동지 — 14개 블록. ` +
      `Header1 × 5, Instruction × 8, LastCard. ` +
      `Image × 1 (꾸러미 도식 placeholder), Quiz × 1 (bundle_quiz).`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}
// =========================
// Scenario: Raw text → "변화의 시작 (Day 1)" 활동지
// =========================

function buildBeginningOfChangeTask(): TaskEditorState {
  return {
    taskId: "mock-day1-start-of-change-" + rid().slice(0, 4),
    title: "변화의 시작",
    setNumber: 1,
    isKeyActivity: true,
    blocks: [
      // 활동지 제목
      {
        id: "block-title-" + rid(),
        type: "Header1",
        content: "변화의 시작",
      },
      // 1) 위대한 선택
      {
        id: "block-h-great-choice-" + rid(),
        type: "Header1",
        content: "위대한 선택",
      },
      {
        id: "block-great-choice-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "반갑습니다. 아마 마음이 심하게 흔들리고 힘든 시기를 겪다가 이곳까지 찾아오셨을 겁니다. 그리고 이제는 정말 스스로 변해야겠다고 굳게 마음먹으셨겠지요. 그렇지 않았다면 디스턴싱이 아니라 당장 눈앞의 고통만 가려줄 임시방편을 택하셨을 테니까요.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "진정한 변화를 일궈내겠다고 결심한 그 용기에 진심으로 박수를 보냅니다. 앞으로 100일 동안 이곳에서 내면의 힘을 기르며, 인생의 전환점을 맞이하시게 될 겁니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 2) 마법의 알약
      {
        id: "block-h-magic-pill-" + rid(),
        type: "Header1",
        content: "마법의 알약",
      },
      {
        id: "block-magic-pill-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이곳에는 복잡한 감정을 단숨에 잠재워 줄 마법의 알약은 없습니다. 그저 글 몇 편 읽는 것만으로 엄청난 변화가 일어난다는 헛된 약속도 하지 않겠습니다. 마음을 치유하는 일은 하루아침에 뚝딱 이루어지는 가벼운 과정이 아닙니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "하지만 이 프로그램은 당신이 스스로의 치료자가 되어 진정한 변화를 이끌어내도록 도울 수는 있습니다. 이 과정은 상담사나 약물에 기대는 것이 아니라, 내 삶을 이끄는 방법을 배우는 주도적인 훈련이 될 것입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "때로는 이 훈련의 과정 속에서 답답하고 좌절감을 느낄지도 모릅니다. 하지만 인내심을 가지고 끝까지 해낸다면, 그 무엇과도 바꿀 수 없는 단단한 내면의 힘을 얻게 될 것입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "여지껏 삶의 전환점을 맞이한 수많은 사람들도 처음에는 과연 자신이 변할 수 있을지 의심했습니다. 지금 이 단계에서 그런 의문이 드는 것은 매우 당연한 일입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              '"나도 변화할 수 있을까?" 해보지 않으면 모릅니다. 제대로 해보지 않고 평생 "나도 변화할 수 있을까?"라는 의문을 품고 사시겠습니까, 아니면 이번 기회에 나도 변화할 수 있는지 확실히 알아보시겠습니까?',
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "변화하기로 마음 먹고 여기까지 온 것이라면, 지금부터는 스스로를 믿고 프로그램을 밟아 나아가보시길 바랍니다. 포기하고 싶은 순간이 찾아오면 언제든 코치 선생님에게 알려주세요. 코치 선생님은 당신이 이 여정을 계속해 나갈 수 있도록 최선을 다해 도울 것입니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 3) 마음속 타인
      {
        id: "block-h-stranger-" + rid(),
        type: "Header1",
        content: "마음속 타인",
      },
      {
        id: "block-stranger-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "누군가 내 옆을 졸졸 따라다니며 부정적이고 비판적인 말을 귓가에 끊임없이 속삭인다고 상상해 보세요. 처음엔 당황해서 꾹 참아보기도 하고, 그래도 멈추지 않으면 조용히 하라고 화도 내보겠지요. 하지만 그는 아랑곳하지 않습니다. 밀어내려 싸우고 애원해 보지만, 결국 괴로워지는 건 나 자신뿐입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "그 목소리는 내가 어찌할 수 없는 것입니다. 하지만 우리는 이러한 사실을 모른채 스스로에게 끔찍한 일을 저지르고 있습니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "우린 마음속에 떠오르는 모든 생각과 감정을 밀어내려고 온갖 수를 쓰며 애를 씁니다. 그러다 결국엔 그런 부정적인 목소리를 내는 자신에게 화를 내고, 그것을 막지 못하는 스스로를 자책하며 갉아먹고 있는 것이죠.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "앞으로 100일 동안 다룰 핵심 기술은 바로 '거리두기'입니다. 쉽게 말해, 머릿속에 떠오르는 생각들을 내 옆에 앉은 낯선 사람이 던지는 말이라고 여기는 연습입니다. 이 기술에 익숙해지면 생각을 관찰할 수 있게 되고, 귓가에 어떤 소리가 맴돌든 그로부터 온전히 자유로워질 수 있습니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "단, 이것이 단순히 '주변의 잡음처럼 하찮게 여기며 무시하기'를 뜻하진 않는다는 것을 꼭 기억해주세요. 디스턴싱은 그 반대입니다. 하찮게 여기지 않을 것입니다. 그렇다고 소중히 여기지도 않을 것입니다. 판단과 평가 없이 듣고, 있는 그대로를 기꺼이 품을 것입니다. 우리는 앞으로 이 태도를 연습해 나갈 것입니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // Quiz 1
      {
        id: "block-quiz-stranger-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "디스턴싱을 배우고 나면, 머릿속에 스스로를 깎아내리는 비관적인 생각이 떠올랐을 때 어떻게 대처하게 될까요?",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Quiz",
            content: "stranger_quiz",
            options: [
              {
                text: "A. 그 생각이 나의 진짜 모습이자 진실이라고 굳게 믿는다.",
                isCorrect: false,
              },
              {
                text: "B. 나와 무관한 주변의 소리로 여기며 판단 없이 지그시 관찰한다.",
                isCorrect: true,
              },
              {
                text: "C. 왜 그런 생각을 했는지 스스로를 탓하며 분석한다.",
                isCorrect: false,
              },
            ],
            incorrectMessage: "활동지를 천천히 살펴본 뒤 다시 답해보세요.",
          },
        ],
      },
      // Answer 1
      {
        id: "block-answer-stranger-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "정답은 B입니다. 생각은 나의 본질을 의미하지 않습니다. 그저 지금 창 밖에서 들려오는 바람 소리처럼 우연히 스쳐 지나가는 것일 뿐입니다. 이 사실을 꼭 기억해주시길 바랍니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 4) 오래된 펌프
      {
        id: "block-h-pump-" + rid(),
        type: "Header1",
        content: "오래된 펌프",
      },
      {
        id: "block-pump-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "낯선 개념을 받아들이며 습관을 바로잡는 과정은 오랫동안 쓰지 않은 펌프에서 물을 끌어올리는 일과 비슷합니다. 펌프를 3년이나 방치했다면 그 안에는 엄청난 녹과 먼지가 쌓여 있겠지요. 즉, 누구나 거리두기를 연습하며 내면을 들여다보기 시작하면, 처음에는 흙먼지가 섞인 탁한 물이 쏟아져 나옵니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "많은 분들이 이 흙탕물을 보고는 상태가 더 나빠졌다고 지레짐작하며 서둘러 펌프질을 멈춰버립니다. 하지만 이는 고여 있던 찌꺼기가 빠져나오는, 아주 자연스럽고도 꼭 필요한 중간 과정일 뿐입니다. 지금 해야 할 일은 맑은 물이 나올 때까지 멈추지 않고 계속 펌프질을 하는 것입니다.",
            typographyStyle: "Body",
          },
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "당장 거리두기 연습이 고통스럽고 혼란스럽더라도 두려워하거나 멈추지 않아야 합니다. 내면의 탁한 습관들이 모두 흘러가도록 묵묵히 내버려 두면, 마침내 맑고 건강한 습관이 그 자리를 채울 것입니다. 맑은 물을 마시게 되는 그 감격스러운 순간까지 프로그램이 곁에서 돕겠습니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // Quiz 2
      {
        id: "block-quiz-pump-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "훈련을 시작한 후, 갑자기 혼란스럽고 부정적인 감정이 쏟아져 나온다면 어떻게 해야 할까요?",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Quiz",
            content: "pump_quiz",
            options: [
              {
                text: "A. 상태가 악화되었다고 판단하고 즉시 훈련을 그만둔다.",
                isCorrect: false,
              },
              {
                text: "B. 더 쉽고 빠른 다른 치료법이 없는지 찾아 나선다.",
                isCorrect: false,
              },
              {
                text: "C. 자연스러운 과정이라 여기고 훈련을 계속한다.",
                isCorrect: true,
              },
            ],
            incorrectMessage: "활동지를 천천히 살펴본 뒤 다시 답해보세요.",
          },
        ],
      },
      // Answer 2
      {
        id: "block-answer-pump-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "정답은 C입니다. 훈련 과정에서 내면의 찌꺼기가 빠져나오는 것은 자연스럽고 필연적인 현상입니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // 5) 100일간의 여정
      {
        id: "block-h-journey-" + rid(),
        type: "Header1",
        content: "100일간의 여정",
      },
      {
        id: "block-journey-body-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "이 힘든 시기가 오히려 단단한 성장의 발판이 될 수 있도록 끝까지 함께하겠습니다. 프로그램을 성공적으로 마치게 된다면, 훗날 비슷한 시련이 다시 찾아와도 예전처럼 무너지지 않고 의연하게 이겨낼 수 있을 것입니다. 이 100일간의 뜻깊은 여정 끝에서, 한층 성숙하고 달라진 자신과 마주하시게 될 겁니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // Quiz 3
      {
        id: "block-quiz-journey-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "100일간의 디스턴싱 훈련 과정을 통해 얻을 수 있는 것은 무엇일까요?",
            typographyStyle: "Label",
          },
          {
            id: "line-" + rid(),
            type: "Quiz",
            content: "journey_quiz",
            options: [
              {
                text: "A. 당장의 불안과 스트레스를 잊게 해주는 도피처",
                isCorrect: false,
              },
              {
                text: "B. 근본적으로 나 자신을 변화시키는 내면의 힘",
                isCorrect: true,
              },
              {
                text: "C. 누군가의 조언으로 내 문제를 해결하는 것",
                isCorrect: false,
              },
            ],
            incorrectMessage: "활동지를 천천히 살펴본 뒤 다시 답해보세요.",
          },
        ],
      },
      // Answer 3
      {
        id: "block-answer-journey-" + rid(),
        type: "Instruction",
        content: "",
        lines: [
          {
            id: "line-" + rid(),
            type: "Typography",
            content:
              "정답은 B입니다. 디스턴싱은 스스로 만들어내는 가장 확실하고 굳건한 변화를 추구합니다.",
            typographyStyle: "Body",
          },
        ],
      },
      // LastCard
      {
        id: "task-editor-last-card",
        type: "LastCard",
        content: "common:lastCard1\ncommon:lastCard2",
      },
    ],
  };
}

async function* rawToBeginningScenario(
  tid: string,
  rawText: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield ev("tool_call", { name: "ls", args: { path: "/guides" } });
  await sleep(400, signal);
  yield ev("tool_result", {
    name: "ls",
    preview:
      "00-overview.md\n01-components.md\n02-structure.md\n03-tone.md\n04-styles.md\nexamples/\n  day1-start-of-change.json\n  perception-rain-r.json\n  allow-rain-a.json",
  });

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/03-tone.md",
    `# Tone Guide

- 반말 X. 존댓말 사용
- "~합니다" 보다 "~해요" 가 자연스러움
- 1~2문장씩 짧게 끊어쓰기
- 사용자를 다그치지 않음 — 권유형 어미 ("~해볼까요")
- Label 은 질문/지시, Body 는 설명, Dimmed 는 예시·부가, Alert 는 강조`,
    signal
  );
  yield* toolReadFile(
    "/guides/examples/day1-start-of-change.json",
    '{\n  taskId: "day1-start-of-change",\n  title: "변화의 시작",\n  blocks: [Header1 (title), Header1 + Instruction × 2 (5 sections), Quiz × 3 each followed by answer Body, LastCard]\n}',
    signal
  );

  const charCount = rawText.length;

  yield* streamReply(
    `${charCount}자, "변화의 시작" 활동지 (디스턴싱 100일 프로그램 Day 1) 로 분석됩니다.\n\n` +
      `소스 마크업 (제목 / 소제목 / 기본 / 버튼) 을 가이드 위계에 매핑했어요:\n` +
      `· "제목" → 활동지 title 필드 + 첫 Header1 블록\n` +
      `· "소제목" → 섹션 Header1 블록\n` +
      `· "기본" → Instruction 블록의 Typography(Body) 라인\n` +
      `· "버튼" + 옵션 + "활동지를 천천히 살펴본 뒤 다시 답해보세요." → Quiz 라인 (incorrectMessage 포함)\n\n` +
      `구조 설계:\n` +
      `• Header1 × 6 — 활동지 제목 + 5개 섹션 (위대한 선택 / 마법의 알약 / 마음속 타인 / 오래된 펌프 / 100일간의 여정)\n` +
      `• Instruction × 11 — 본문 5개 + Quiz 3개 + 정답 해설 3개\n` +
      `• Typography(Body) × 16, Typography(Label) × 3\n` +
      `• Quiz × 3 — stranger_quiz (정답 B), pump_quiz (정답 C), journey_quiz (정답 B). 모두 incorrectMessage 동일\n` +
      `• LastCard\n\n` +
      `Quiz 직후에는 가이드 패턴대로 정답 해설 Instruction 을 배치했습니다.`,
    signal
  );

  const draftTask = buildBeginningOfChangeTask();
  session.currentTask = draftTask;

  const action = writeFileAction(
    draftTask,
    `"변화의 시작" 활동지 (Day 1) — 18개 블록. ` +
      `Header1 × 6, Instruction × 11, LastCard. ` +
      `Quiz × 3 (stranger_quiz / pump_quiz / journey_quiz), 모두 정답 해설 Instruction 동반.`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Branching flow with ButtonGroup (showFromId)
// =========================

async function* branchingFlowScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield* toolReadFile(
    "/guides/01-components.md",
    MOCK_GUIDES.components,
    signal
  );
  yield* toolReadFile(
    "/guides/02-structure.md#branching",
    `## 분기 (ButtonGroup) 규칙\n\n- showFromId/hideFromId/showToId/restartFromId 는 실재 블록 id 여야 함.\n- 분기 대상 블록은 ButtonGroup 바로 뒤에 배치.\n- 모든 옵션 경로가 결국 LastCard 로 수렴.`,
    signal
  );

  const base = ensureCurrentTask(session);

  yield* streamReply(
    "ButtonGroup 분기를 추가할게요. 가이드 규칙대로 옵션의 showFromId 는 실재하는 새 블록을 가리키게 하고, 두 경로 모두 LastCard 로 수렴하도록 LastCard 직전에 삽입합니다.",
    signal
  );

  // 분기 후 표시될 두 경로 블록을 먼저 만들고, 그 id 를 ButtonGroup option 의 showFromId 로 연결
  const yesId = "block-branch-yes-" + rid();
  const noId = "block-branch-no-" + rid();

  const branchQuestionBlock: TaskEditorBlock = {
    id: "block-branch-question-" + rid(),
    type: "Instruction",
    content: "",
    lines: [
      {
        id: "line-" + rid(),
        type: "Typography",
        content: "지금 그 감정을 잠시 머물게 둘 준비가 되셨나요?",
        typographyStyle: "Label",
      },
      {
        id: "line-" + rid(),
        type: "ButtonGroup",
        content: "ready_to_allow",
        options: [
          { text: "네, 시도해볼게요", showFromId: yesId },
          { text: "아직 어려워요", showFromId: noId },
        ],
      },
    ],
  };

  const yesPathBlock: TaskEditorBlock = {
    id: yesId,
    type: "Instruction",
    content: "",
    lines: [
      {
        id: "line-" + rid(),
        type: "Typography",
        content: "좋습니다. 1분간 그 감정에 이름표를 붙이고 지켜봐주세요.",
        typographyStyle: "Body",
      },
      {
        id: "line-" + rid(),
        type: "Textarea",
        content: "allow_observation",
        textareaPlaceholder: "1분 후, 감정이 어떻게 변했나요?",
      },
    ],
  };

  const noPathBlock: TaskEditorBlock = {
    id: noId,
    type: "Instruction",
    content: "",
    lines: [
      {
        id: "line-" + rid(),
        type: "Typography",
        content:
          "괜찮아요. 어려운 게 자연스럽습니다. 다음에 다시 시도해도 좋아요.",
        typographyStyle: "Body",
      },
      {
        id: "line-" + rid(),
        type: "Typography",
        content: "지금 가장 부담스럽게 느껴지는 부분은 무엇인가요?",
        typographyStyle: "Label",
      },
      {
        id: "line-" + rid(),
        type: "Textarea",
        content: "allow_barrier",
        textareaPlaceholder: "솔직하게 적어주세요",
      },
    ],
  };

  // LastCard 직전에 세 블록 순서대로 삽입
  let nextTask = insertBeforeLastCard(base, branchQuestionBlock);
  nextTask = insertBeforeLastCard(nextTask, yesPathBlock);
  nextTask = insertBeforeLastCard(nextTask, noPathBlock);

  session.currentTask = nextTask;

  const action = writeFileAction(
    nextTask,
    `ButtonGroup 분기 추가 — 질문 (ready_to_allow) + 두 경로 블록 (id="${yesId}" / "${noId}"). ` +
      `options[0].showFromId → yes path, options[1].showFromId → no path. ` +
      `두 경로 모두 LastCard 로 수렴.`
  );
  session.pendingAction = action;
  yield pendingApprovalEvent(tid, action);
  yield debugEvent(tid, t0);
}

// =========================
// Scenario: Validate task against design guide (read-only)
// =========================

async function* validateTaskScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const session = getSession(tid);
  const t0 = Date.now();

  yield* toolReadFile("/guides/02-structure.md", MOCK_GUIDES.structure, signal);
  yield ev("tool_call", {
    name: "read_file",
    args: { file_path: "/task.json" },
  });
  await sleep(400, signal);

  const base = ensureCurrentTask(session);
  yield ev("tool_result", {
    name: "read_file",
    preview: `(task with ${base.blocks.length} blocks)`,
  });

  // 가이드 규칙 lint
  type Issue = { level: "error" | "warn" | "ok"; msg: string };
  const issues: Issue[] = [];

  // 1) taskId / title
  if (!base.taskId.trim()) {
    issues.push({ level: "error", msg: "taskId 가 비어 있음" });
  } else if (!/^[a-z0-9][a-z0-9-]*$/.test(base.taskId)) {
    issues.push({
      level: "warn",
      msg: `taskId "${base.taskId}" 가 kebab-case 패턴이 아님`,
    });
  }
  if (!base.title.trim()) {
    issues.push({ level: "error", msg: "title 이 비어 있음" });
  }

  // 2) blocks 비어있지 않음
  if (base.blocks.length === 0) {
    issues.push({ level: "error", msg: "blocks 가 비어 있음" });
  }

  // 3) 고유 id
  const seen = new Map<string, number>();
  const lineSeen = new Map<string, number>();
  base.blocks.forEach((b) => {
    seen.set(b.id, (seen.get(b.id) ?? 0) + 1);
    b.lines?.forEach((l) => lineSeen.set(l.id, (lineSeen.get(l.id) ?? 0) + 1));
  });
  seen.forEach((count, id) => {
    if (count > 1) {
      issues.push({
        level: "error",
        msg: `block.id 중복: "${id}" (×${count})`,
      });
    }
  });
  lineSeen.forEach((count, id) => {
    if (count > 1) {
      issues.push({ level: "error", msg: `line.id 중복: "${id}" (×${count})` });
    }
  });

  // 4) LastCard 마지막
  const last = base.blocks[base.blocks.length - 1];
  if (!last || last.type !== "LastCard") {
    issues.push({ level: "error", msg: "마지막 블록이 LastCard 가 아님" });
  } else if (last.id !== "task-editor-last-card") {
    issues.push({
      level: "warn",
      msg: `LastCard id 가 "task-editor-last-card" 가 아님 (현재: "${last.id}")`,
    });
  }

  // 5) Instruction 위계 — Typography 최소 1개
  base.blocks.forEach((b) => {
    if (b.type !== "Instruction") return;
    const typoCount =
      b.lines?.filter((l) => l.type === "Typography").length ?? 0;
    if (typoCount === 0) {
      issues.push({
        level: "error",
        msg: `Instruction "${b.id}" 에 Typography 라인이 없음 (설명 파트 필수)`,
      });
    }
  });

  // 6) Textarea / Preset content snake_case
  const SNAKE = /^[a-z][a-z0-9_]*$/;
  base.blocks.forEach((b) => {
    b.lines?.forEach((l) => {
      if (l.type !== "Textarea" && l.type !== "Preset") return;
      if (!SNAKE.test(l.content)) {
        issues.push({
          level: "warn",
          msg: `${l.type} (line ${l.id}) content "${l.content}" 가 snake_case 가 아님`,
        });
      }
    });
  });

  // 7) ButtonGroup showFromId 가 실재 block id 인지
  const blockIds = new Set(base.blocks.map((b) => b.id));
  base.blocks.forEach((b) => {
    b.lines?.forEach((l) => {
      if (l.type !== "ButtonGroup") return;
      (l.options ?? []).forEach((opt, oi) => {
        const refs: [string, string | undefined][] = [
          ["showFromId", opt.showFromId],
          ["showToId", opt.showToId],
          ["hideFromId", opt.hideFromId],
          ["restartFromId", opt.restartFromId],
        ];
        refs.forEach(([key, value]) => {
          if (value && !blockIds.has(value)) {
            issues.push({
              level: "error",
              msg: `ButtonGroup option[${oi}].${key} = "${value}" — 실재하지 않는 block id`,
            });
          }
        });
      });
    });
  });

  const errorCount = issues.filter((i) => i.level === "error").length;
  const warnCount = issues.filter((i) => i.level === "warn").length;

  const summaryLine =
    errorCount === 0 && warnCount === 0
      ? "✓ 모든 가이드 규칙 통과"
      : `${errorCount === 0 ? "✓" : "✕"} error ${errorCount}, warn ${warnCount}`;

  const detail = issues.length
    ? "\n\n" +
      issues
        .map(
          (i) =>
            `${i.level === "error" ? "✕" : i.level === "warn" ? "⚠" : "✓"} ${i.msg}`
        )
        .join("\n")
    : "";

  yield* streamReply(
    `현재 활동지를 가이드 규칙으로 검증했어요.\n\n` +
      `검사 항목: taskId 패턴 / 빈 필드 / 블록·라인 ID 고유 / LastCard 위치 · id / Instruction Typography 위계 / Textarea·Preset snake_case / ButtonGroup showFromId 참조 무결성.\n\n` +
      `결과: ${summaryLine}${detail}\n\n` +
      `이 시나리오는 읽기 전용입니다 — 자동 수정은 하지 않아요.`,
    signal
  );

  yield ev("final", {
    thread_id: tid,
    assistant_message: `검증 완료. ${summaryLine}`,
    task: base,
    task_changed: false,
  });
  yield debugEvent(tid, t0);
}

// =========================
// Generic — help (mock 안내 + 실제 운영 Agent 역할)
// =========================

async function* genericScenario(
  tid: string,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const t0 = Date.now();
  yield* toolReadFile("/guides/00-overview.md", MOCK_GUIDES.components, signal);

  const reply =
    "━━━ Mock 모드 안내 ━━━\n" +
    "지금 채팅은 실서버 연결 없이 동작하는 mock 응답입니다.\n" +
    "`REACT_APP_AGENT_API_HOST` 환경변수가 설정되면 실서버 SSE 로 자동 전환됩니다.\n\n" +
    "━━━ 운영 Agent 역할 ━━━\n" +
    "DeepAgents + LangGraph 기반의 활동지(TaskEditorState) 작성·편집 어시스턴트.\n" +
    "내부적으로는 다음 가이드 문서를 참조해 작업합니다:\n" +
    "  · 00-overview.md — 전체 개요\n" +
    "  · 01-components.md — 블록/셀 카탈로그 (아래 [컴포넌트] 참고)\n" +
    "  · 02-structure.md — 위계 원칙, ID 규칙, 분기 규칙\n" +
    "  · 03-tone.md — 어투 가이드\n" +
    "  · 04-styles.md — Typography 스타일 가이드\n" +
    "  · examples/*.json — 참고 활동지 예시들\n\n" +
    "[블록 (block.type)]\n" +
    "  · Header1 — 섹션 제목. content 가 한 줄 텍스트. lines 없음.\n" +
    "  · Instruction — 본문 단위. lines 안에 셀들이 있음. 설명 파트(Typography 필수) + 유저 액션 파트(옵셔널).\n" +
    '  · LastCard — 활동지 종료. content 고정 "common:lastCard1\\ncommon:lastCard2". id 고정 "task-editor-last-card".\n' +
    "  마지막 블록은 반드시 LastCard.\n\n" +
    "[셀 (line.type) — 설명 파트]\n" +
    "  · Typography — 텍스트 출력. typographyStyle: Body | Label | Alert | Dimmed\n" +
    "  · Image — 이미지 (content = URL)\n" +
    "  · AutomaticThought — 자동적 사고 mock (도메인 전용)\n\n" +
    "[셀 (line.type) — 액션 파트]\n" +
    "  · Textarea — 자유 입력 (content = snake_case 바인딩 ID, textareaPlaceholder)\n" +
    "  · Preset — 코치 전용 미리채움 입력 (content = snake_case 바인딩 ID)\n" +
    "  · ButtonGroup — 분기형 버튼. options[].showFromId/hideFromId/showToId/restartFromId 로 흐름 제어\n" +
    "  · ButtonSelection — 단순 선택 (분기 없음). allowMultipleSelection 으로 단일/다중\n" +
    "  · Quiz — 정답이 있는 선택. options[] 중 최소 1개 isCorrect: true. 보통 incorrectMessage 동반\n\n" +
    "[Typography 스타일]\n" +
    "  · Body — 일반 본문 (기본)\n" +
    "  · Label — 입력 필드 바로 위 질문/라벨\n" +
    "  · Alert — 주의·중요 안내\n" +
    "  · Dimmed — 부가 설명·힌트\n\n" +
    "[블록 위계 — Instruction]\n" +
    "  Instruction\n" +
    "  ├── 설명 파트 (REQUIRED)\n" +
    "  │   └── Typography(Body/Label/Alert/Dimmed) 최소 1개 + 옵셔널 Image / AutomaticThought\n" +
    "  └── 유저 액션 파트 (OPTIONAL)\n" +
    "      └── Textarea / Preset / ButtonGroup / ButtonSelection / Quiz\n" +
    "  · 액션 라인만 있고 Typography 없으면 검증 실패 (POST /task/drafts 422)\n" +
    "  · 일반적으로 설명 라인 먼저 → 액션 라인 뒤\n\n" +
    "[ID 명명 규칙]\n" +
    '  · taskId: 영문 kebab-case (예: "day1-start-of-change")\n' +
    '  · block.id: "block-<설명>" 또는 "task-editor-block-<slug>"\n' +
    '  · line.id: "line-<설명>" 또는 "task-editor-line-<slug>"\n' +
    "  · Textarea/Preset content (데이터 바인딩): snake_case (예: recent_situation)\n" +
    '  · LastCard.id: 고정 "task-editor-last-card"\n' +
    "  · 한 활동지 안 모든 block.id / line.id 고유\n" +
    "  · 편집 시 기존 id 최대한 보존, 새 요소만 새 id\n\n" +
    "[블록 구성 패턴]\n" +
    "  1. Header1 로 섹션 시작\n" +
    "  2. 본문은 Instruction 안 Typography(Body)\n" +
    "  3. 입력 필드 앞에는 Typography(Label) 로 질문\n" +
    "  4. 주의·강조는 Typography(Alert)\n" +
    "  5. Quiz 다음에는 정답 해설 Instruction 블록\n" +
    "  6. 마지막은 LastCard\n\n" +
    "[ButtonGroup 분기 규칙]\n" +
    "  · showFromId / hideFromId / showToId / restartFromId 는 실재 블록 id 만 가리킴 (매칭 실패 시 검수 거부)\n" +
    "  · 분기 대상 블록은 ButtonGroup 바로 뒤\n" +
    "  · 모든 옵션 경로가 결국 LastCard 로 수렴\n\n" +
    "[Quiz 규칙]\n" +
    "  · options[] 에 최소 1개 isCorrect: true\n" +
    "  · 정답 해설은 Quiz 바로 다음 블록의 Typography(Body) 로 제공\n\n" +
    "[HITL — Human-In-The-Loop]\n" +
    "  · write_file / edit_file 호출 직전 자동 인터럽트 → 사용자가 Approve / Edit / Reject 결정\n" +
    "  · Reject 시 reason 을 ToolMessage 로 agent 에 전달, 다른 접근 시도\n" +
    "  · read_file / ls / grep 같은 정보 수집은 자동 진행\n\n" +
    "━━━ 현재 mock 시나리오 칩 ━━━\n" +
    "• 긴 글 → 활동지 (세 종류: 변화의 시작 / 거리두기란 무엇인가 / 기술 1: 경험 분리)\n" +
    "• 분기 추가 (ButtonGroup + showFromId 두 경로)\n" +
    "• 검증 (현재 task 를 가이드 규칙으로 lint, 읽기 전용)\n" +
    "• Quiz 1줄 추가\n" +
    "• 마지막 블록 삭제\n\n" +
    "원하시는 작업을 자유롭게 적어주시거나, 아래 칩을 눌러보세요.";

  yield* streamReply(reply, signal);

  yield ev("final", {
    thread_id: tid,
    assistant_message: reply,
    task: getSession(tid).currentTask,
    task_changed: false,
  });
  yield debugEvent(tid, t0);
}

// =========================
// Resume
// =========================

export async function* mockResumeTaskAuthoring(
  req: ResumeRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const tid = req.thread_id;
  const session = getSession(tid);
  const action = session.pendingAction;
  const decision: Decision | undefined = req.decisions[0];
  const t0 = Date.now();

  if (!action || !decision) {
    yield ev("error", {
      thread_id: tid,
      type: "no_pending_action",
      message: "No pending action to resume.",
    });
    yield debugEvent(tid, t0);
    return;
  }

  if (decision.type === "approve") {
    yield ev("tool_result", { name: action.name, preview: "OK" });
    await sleep(180, signal);
    const reply = "적용했어요. 미리보기에서 확인해보세요.";
    yield* streamReply(reply, signal);
    if (session.currentTask) {
      yield ev("task_patch", {
        thread_id: tid,
        task: session.currentTask,
      });
    }
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: session.currentTask,
      task_changed: !!session.currentTask,
    });
  } else if (decision.type === "edit") {
    // edited_action 으로 task 재계산
    const updated = applyMockEdit(session.currentTask, decision.edited_action);
    session.currentTask = updated;
    yield ev("tool_result", {
      name: decision.edited_action.name,
      preview: "OK (edited)",
    });
    await sleep(180, signal);
    const reply = "수정하신 내용으로 적용했어요.";
    yield* streamReply(reply, signal);
    if (updated) {
      yield ev("task_patch", { thread_id: tid, task: updated });
    }
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: updated,
      task_changed: !!updated,
    });
  } else {
    // reject
    const why = decision.message?.trim() || "다른 방식으로 해주세요";
    const reply = `알겠어요. "${why}" — 다시 시도해볼게요.`;
    yield* streamReply(reply, signal);
    yield ev("final", {
      thread_id: tid,
      assistant_message: reply,
      task: session.currentTask,
      task_changed: false,
    });
  }

  yield debugEvent(tid, t0);
  session.pendingAction = undefined;
}
